import { useContext } from 'react';
import { useFormik } from 'formik';
import { useSearchParams } from 'next/navigation';
import styled, { css } from 'styled-components';

import Dropdown from '@/components/core/form/input/dropdown';
import { PrimaryButton } from '@/components/ui/Button';
import ClientContext from '@/contexts/client.context';
import { getClient } from '@/services/client.service';
import getCountry from '@/utils/get-country';
import { getOnboardingURL } from '@/utils/onboarding-urls';
import { trackClientEligibilityChecked } from '@/utils/track';
import { trackEvent } from '@/lib/gtm';

export default function FormComponent({ countries, buttonLabel, product, id }) {
  const { client, setSelectClientCountry } = useContext(ClientContext);
  const searchParams = useSearchParams();

  const formik = useFormik({
    isInitialValid: false,
    initialValues: {
      country: null,
    },
    onSubmit: async (values) => {
      const cl = await getClient({ client: client.clientKey, productKey: product?.key });

      trackEvent('redirect_to_onboarding', {
        value: client.clientKey,
      });

      const URL = getOnboardingURL({
        client: cl,
        country: values.country,
        query: Object.fromEntries(searchParams),
      });

      trackClientEligibilityChecked({
        clientExists: true,
        clientCountry: values.country.key,
        clientId: cl.clientKey,
        clientName: cl.name,
      });

      window.location = URL;
    },
  });

  const handleSelect = (value) => {
    formik.setValues({ country: value });
    setSelectClientCountry({ country: value });
  };

  if (!countries || !countries.length) return null;

  return (
    <Form data-testid="onboarding-form" onSubmit={formik.handleSubmit}>
      <StyledDropdown
        name="country"
        label="Select your region/country"
        placeholder="Region/country"
        options={countries?.map((country) => getCountry(country.country))}
        value={formik.values.country}
        onChange={(value) => handleSelect(value)}
      />

      <PrimaryButton
        id={`${id}-button`}
        type="submit"
        loading={formik.isSubmitting}
        disabled={!formik.isValid}
      >
        {buttonLabel || 'Get started'}
      </PrimaryButton>
    </Form>
  );
}

const Form = styled.form`
  max-width: 350px;

  ${(props) =>
    props.xl &&
    css`
      max-width: 700px;
      text-align: center;
      margin: auto;
    `};

  @media (max-width: ${(props) => props.theme.breakpoints.max_sm}px) {
    text-align: center;
    margin: auto;
  }
`;

const StyledDropdown = styled(Dropdown)`
  > div {
    color: #3e424c;
  }
`;
